import AppBar from '../components/AppBar'
import Avatar from '../components/Avatar'
import { Notification } from '../types/notification'
import { NotificationType } from '../types/notificationType'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '../stores/notificationStore'
import { useAuthStore } from '../stores/authStore'
import LoadingSpinner from '../components/LoadingSpinner'
import Gap from '../components/Gap'

/**
 * 보낸 사람이 없는 알림의 프로필 사진. 관리자가 보내는 것이라 TASTEE 표식을 쓴다.
 *
 * 앱의 tastee 계정 프로필과 같은 그림이지만 **그 계정에서 가져오지 않는다.**
 * 공지·광고 알림에는 보낸 사람(`relatedUser`)이 없어서 서버가 사진을 못 준다.
 * 그래서 여기 박아 둔 파일이 곧 관리자의 얼굴이다 — 앱에서 tastee 프로필을
 * 바꿔도 이 그림은 안 바뀐다. 바꾸려면 이 파일을 새 이름으로 올려야 한다.
 *
 * 이름 뒤 `-v2`: public/images 는 1년 immutable 로 나간다. 같은 이름에
 * 덮어쓰면 이미 받아 간 기기에서는 옛 그림이 계속 보인다.
 */
const ADMIN_AVATAR = '/images/notification-admin-v2.png'

const adminAvatar = (type: string): string | null =>
  type === NotificationType.SERVICE_NOTICE || type === NotificationType.MARKETING
    ? ADMIN_AVATAR
    : null

/**
 * 한 줄 알림에서 굵게 보여줄 곳.
 *
 * 둘 다 '누구/무엇'이 앞에 오고 그 뒤에 무슨 일이 있었는지가 붙는 꼴이라,
 * 앞부분만 굵게 뽑는다.
 *
 *   홍길동님이 회원님의 기록을 좋아합니다
 *   제안하신 원블룸이 등록되었어요
 *
 * 가게 이름 쪽은 조사('이'/'가')를 이름에서 떼어내야 한다. 이름 자체가
 * '이'로 끝날 수 있어서(모리야이), 끝에서 한 글자만 조사로 본다.
 */
// 세 조각으로 끊는다 — 앞말, 굵게 할 곳, 뒷말
const EMPHASIS_PATTERNS = [
  /^()(.+?)(님이.*)$/,
  /^(제안하신 )(.+)([이가] 등록되었어요)$/,
]

const emphasize = (title: string): React.ReactNode => {
  for (const pattern of EMPHASIS_PATTERNS) {
    const match = title.match(pattern)
    if (!match) continue

    const [, lead, strong, tail] = match
    return (
      <>
        {lead}
        <strong className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main">
          {strong}
        </strong>
        {tail}
      </>
    )
  }

  return title
}

const NotificationList: React.FC = () => {
  const { notifications, isLoading, loadNotifications, markAllAsRead } =
    useNotificationStore()
  const navigate = useNavigate()
  const userId = useAuthStore((state) => state.userId)

  // Pull-to-refresh
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const pullStartY = useRef(0)
  const isPulling = useRef(false)
  const PULL_THRESHOLD = 80

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await loadNotifications()
    setIsRefreshing(false)
  }, [loadNotifications])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollEl = scrollRef.current
    if (!scrollEl || scrollEl.scrollTop > 0) return
    pullStartY.current = e.touches[0].clientY
    isPulling.current = true
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current) return
    const scrollEl = scrollRef.current
    if (!scrollEl || scrollEl.scrollTop > 0) {
      isPulling.current = false
      setPullDistance(0)
      return
    }
    const distance = Math.max(0, e.touches[0].clientY - pullStartY.current)
    setPullDistance(Math.min(distance * 0.6, 150))
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (!isPulling.current) return
    isPulling.current = false
    if (pullDistance >= PULL_THRESHOLD) {
      handleRefresh()
    }
    setPullDistance(0)
  }, [pullDistance, handleRefresh])

  useEffect(() => {
    loadNotifications()
    markAllAsRead()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickNotification = async (notification: Notification) => {
    // 알림 타입에 따라 해당 페이지로 이동
    if (notification.type === NotificationType.FOLLOW_REQUEST) {
      // 팔로우 요청 알림: 팔로우 요청 페이지로 이동
      navigate('/follow-requests')
    } else if (notification.type === NotificationType.FOLLOW_ACCEPT) {
      if (notification.relatedUserId) {
        navigate(`/user/${notification.relatedUserId}/profile`)
      }
    } else if (notification.type === NotificationType.FOLLOW) {
      // 공개 계정 팔로우 알림: 팔로워의 프로필로 이동
      if (notification.relatedUserId) {
        navigate(`/user/${notification.relatedUserId}/profile`)
      }
    } else if (
      notification.type === NotificationType.COMMENT ||
      notification.type === NotificationType.LIKE
    ) {
      // 댓글 또는 좋아요 알림: 리뷰 상세 페이지로 이동
      if (notification.relatedReviewId) {
        navigate(`/review-detail/${notification.relatedReviewId}`)
      }
    } else if (
      notification.type === NotificationType.BOOKMARK ||
      notification.type === NotificationType.VISIT
    ) {
      if (notification.relatedReviewId) {
        navigate(`/review-detail/${notification.relatedReviewId}`)
      } else if (notification.relatedPlaceId) {
        navigate(`/store-review/${notification.relatedPlaceId}`)
      }
    } else if (
      notification.type === NotificationType.TAG ||
      notification.type === NotificationType.MENTION
    ) {
      // 태그·언급 알림: 해당 기록으로 이동
      if (notification.relatedReviewId) {
        navigate(`/review-detail/${notification.relatedReviewId}`)
      }
    } else if (notification.type === NotificationType.PLATE_PLACE_ADD) {
      // 플레이트 장소 추가 알림: 지도 화면에서 추가된 장소의 바텀시트를 띄움
      if (notification.relatedPlaceId) {
        navigate(`/main-map?placeId=${notification.relatedPlaceId}`)
      }
    } else if (notification.type === NotificationType.INQUIRY_REPLY) {
      // 답변 알림: 그 문의의 대화로 바로 들어간다.
      // 문의가 지워졌으면(탈퇴) 알림도 cascade 로 같이 사라지므로 여기 올 일이 없다.
      if (notification.relatedInquiryId) {
        navigate(`/support/${notification.relatedInquiryId}`)
      } else {
        navigate('/support/list')
      }
    } else if (
      notification.type === NotificationType.SERVICE_NOTICE ||
      notification.type === NotificationType.MARKETING
    ) {
      /*
       * 서비스 고지·광고는 목적지가 정해져 있지 않다.
       *
       * 푸시로 받으면 서버가 실어 보낸 target_path로 가지만, 알림 목록에서
       * 누를 때는 그 값이 없다. 그래서 딸려온 값 중 가장 구체적인 것으로 간다.
       * 아무것도 없으면 홈 — 눌렀는데 아무 일도 안 일어나면 고장으로 보인다.
       */
      if (notification.relatedPlaceId) {
        // 장소가 딸린 고지는 '제안한 가게가 등록됐다'가 대부분이다.
        // 지도에 띄워 보여주는 것보다 바로 기록 화면으로 보내는 편이
        // 사용자가 하려던 일에 가깝다.
        navigate(`/store-review/${notification.relatedPlaceId}`)
      } else if (notification.relatedReviewId) {
        navigate(`/review-detail/${notification.relatedReviewId}`)
      } else if (notification.relatedUserId) {
        navigate(`/user/${notification.relatedUserId}/profile`)
      } else {
        navigate('/main-home')
      }
    } else if (notification.type === NotificationType.PLATE_INVITE) {
      // 플레이트 초대 알림: 초대된 플레이트리스트로 이동
      if (notification.relatedCategoryId && userId) {
        navigate(`/user/${userId}/plate-category/${notification.relatedCategoryId}`)
      } else {
        navigate('/main-my')
      }
    }
  }

  const renderLoading = () => {
    return (
      /*
        첫 진입 로딩을 빈 문구와 같은 자리에 둔다. 감싼 칸이 flex flex-col 이라
        빈 문구와 똑같이 flex-1 을 쓴다.
        당겨서 새로고침 스피너(310행)는 상단 띠 안이 제자리라 건드리지 않는다.
      */
      <div className="w-full flex-1 flex items-center justify-center empty-state-center">
        <LoadingSpinner size={28} borderWidth={4} />
      </div>
    )
  }

  const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return ''

    // 백엔드에서 "Y.m.d H:i" 형식으로 오는 날짜를 파싱 가능한 형식으로 변환
    // 예: "2026.02.04 14:30" -> "2026-02-04T14:30"
    const normalizedDateString = dateString
      .replace(/\./g, '-')
      .replace(' ', 'T')

    const now = new Date()
    const date = new Date(normalizedDateString)

    // 유효하지 않은 날짜인지 확인
    if (isNaN(date.getTime())) {
      return ''
    }

    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)

    // 24시간 미만이면 상대 시간으로 표시
    if (diffHours < 24) {
      if (diffHours > 0) {
        return `${diffHours}시간 전`
      } else if (diffMinutes > 0) {
        return `${diffMinutes}분 전`
      } else {
        return `${diffSeconds}초 전`
      }
    }

    // 24시간 이상이면 날짜로 표시 (예: 12월 31일)
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${month}월 ${day}일`
  }

  const renderEmptyState = () => {
    return (
      <div className="w-full flex-1 flex flex-col items-center justify-center empty-state-center">
        <div className="text-style-body1 text-textColorText3">
          알림이 없습니다.
        </div>
      </div>
    )
  }

  const renderNotificationList = () => {
    if (notifications.length === 0) {
      return renderEmptyState()
    }

    return notifications.map((notification) => (
      <div
        key={notification.id}
        className="w-full flex px-20px pt-16px pb-8px cursor-pointer"
        onClick={() => handleClickNotification(notification)}
      >
        {/* 이미지 */}
        <Avatar
          nickname={notification.title}
          profileImage={notification.image ?? adminAvatar(notification.type)}
          size={48}
        />

        <Gap width={12} />

        <p className="font-pretendard font-regular text-16px leading-24px text-brown-80%-main break-all">
          {emphasize(notification.title)}
          &nbsp;&nbsp;
          {/* 날짜 */}
          <strong className="font-pretendard font-regular text-16px leading-180% text-918063-80%">
            {formatRelativeTime(notification.createdAt)}
          </strong>
        </p>
      </div>
    ))
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* 앱바 */}
      <AppBar title="알림" isBottomBorder />

      <div
        ref={scrollRef}
        className="w-full flex-1 flex flex-col overflow-y-auto scrollbar-hide"
        style={{ overscrollBehavior: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh 인디케이터 */}
        <div
          className="w-full flex items-center justify-center shrink-0"
          style={{
            height: isRefreshing ? 100 : pullDistance > 0 ? pullDistance : 0,
            minHeight: isRefreshing ? 100 : 0,
            transition: isPulling.current ? 'none' : 'height 0.2s ease, min-height 0.2s ease',
            overflow: 'hidden',
          }}
        >
          {isRefreshing ? (
            <LoadingSpinner size={28} borderWidth={3} />
          ) : pullDistance > 0 ? (
            <div
              className="text-textColorText3 text-12px font-pretendard"
              style={{
                opacity: Math.min(pullDistance / PULL_THRESHOLD, 1),
              }}
            >
              ↓
            </div>
          ) : null}
        </div>
        {/* grow shrink-0 — 빈 상태 문구를 칸 중앙에 놓기 위해서다.
            이 칸은 스크롤 영역의 flex 자식인데 flex 지시가 없으면 높이가
            내용만 해진다. 그러면 안쪽 빈 상태의 flex-1 이 글자 높이로
            풀려서 문구가 중앙이 아니라 맨 위에 붙는다(empty-state-center
            설명과 같은 함정). grow 로 빈 칸을 채우고, shrink-0 으로
            알림이 많을 때 목록이 눌리지 않게 한다. */}
        <div className="app-container flex flex-col grow shrink-0">
          {/* 이미 받아둔 알림이 있으면 다시 조회 중이어도 그대로 둔다.
              스피너로 덮으면 돌아올 때마다 목록이 사라졌다 나타난다 */}
          {isLoading && !isRefreshing && notifications.length === 0 ? (
            <>
              {/* 로딩 중인 경우 */}
              {renderLoading()}
            </>
          ) : (
            <>
              {/* 알림 리스트 표시 */}
              {renderNotificationList()}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationList
